import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    disabledTypes: { type: Array, default: [] }
  }

  // Maps each search shortcut key to the header-search scope it opens and the
  // record type token used to gate it. Keys whose token is present in
  // `disabledTypesValue` (driven by SearchHelper#disabled_search_types) are
  // skipped so a toggled-off record type's shortcut does nothing.
  static searchTabKeys = {
    p: { scope: "passwords", type: "Password" }, P: { scope: "passwords", type: "Password" },
    c: { scope: "companies", type: "Company" }, C: { scope: "companies", type: "Company" },
    a: { scope: "assets", type: "Asset" }, A: { scope: "assets", type: "Asset" },
    k: { scope: "articles", type: "Article" }, K: { scope: "articles", type: "Article" },
    w: { scope: "websites", type: "Website" }, W: { scope: "websites", type: "Website" },
    o: { scope: "processes", type: "Process" }, O: { scope: "processes", type: "Process" }
  }

  connect() {
    this.boundHandleKeyDown = this.handleKeyDown.bind(this)
    document.addEventListener('keydown', this.boundHandleKeyDown)
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleKeyDown)
    this.boundHandleKeyDown = null
  }

  // Focusing the input opens the anchored panel; clicking a scope menu item
  // presets the type through the same path the dropdown uses
  openSearch(scopeType = null) {
    const searchInput = document.querySelector('#mainSearchInput')
    if (!searchInput) return

    if (scopeType) {
      const scopeItem = document.querySelector(`.header__search-scope [data-scope-type='${scopeType}']`)
      if (scopeItem) scopeItem.click()
    }

    searchInput.focus()
  }

  handleKeyDown(event) {
    // Skip if user is typing in an input or textarea
    if (this.isUserTyping()) return

    // Open search
    if (event.key === '/' && !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey) {
      event.preventDefault()
      this.openSearch()
    }

    // Open advanced search
    if (event.key === '?' && event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey) {
      event.preventDefault()
      const params = new URLSearchParams()
      params.set("back_url", window.location.href)

      const searcher = document.querySelector("[data-controller='searcher']")
      if (searcher && searcher.dataset.companyId) {
        params.set("company", searcher.dataset.companyId)
      }

      window.location.href = `/advanced_search${params.toString() ? `?${params}` : ""}`
      return
    }

    // Open search scoped to a specific type
    if (!event.ctrlKey && !event.metaKey && !event.altKey) {
      const tab = this.constructor.searchTabKeys[event.key]
      if (tab && !this.disabledTypesValue.includes(tab.type)) {
        event.preventDefault()
        this.openSearch(tab.scope)
      }
    }


    // if CTRL + K, open keyboard shortcuts menu
    if (event.key === '/' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      if (document.querySelector('#keyboard-shortcuts-shortcut')) {
        document.querySelector('#keyboard-shortcuts-shortcut').click()
      }
      return;
    }

    // Toggle Hudini. The header link carries the current page as chat context.
    if ((event.key === 'h' || event.key === 'H') && event.shiftKey && !event.altKey && !event.ctrlKey) {
      event.preventDefault()
      this.clickFirst(['#ai-shortcut'])
      return;
    }

    if ((event.key === 'm' || event.key === 'M') && event.shiftKey && !event.altKey && !event.ctrlKey) {
      event.preventDefault()
      if (document.querySelector('#add-comment-shortcut')) {
        document.querySelector('#add-comment-shortcut').click()
      }
      return;
    }

    if ((event.key === 'e' || event.key === 'E') && event.shiftKey && !event.altKey && !event.ctrlKey) {
      event.preventDefault()
      if (document.querySelector('#edit-shortcut')) {
        document.querySelector('#edit-shortcut').click()
      }
      return;
    }

    if ((event.key === 'f' || event.key === 'F') && event.shiftKey && !event.altKey && !event.ctrlKey) {
      event.preventDefault()
      if (document.querySelector('#favorite-shortcut')) {
        document.querySelector('#favorite-shortcut').click()
      }
      return;
    }

    if (event.key === '[' && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault()
      const toggleButtons = Array.from(document.querySelectorAll('.toggle-pane-button'));
      const visibleButton = toggleButtons.find(button => {
        const style = window.getComputedStyle(button);
        return style.display !== 'none';
      });

      if (visibleButton) {
        visibleButton.click();
      }
      return;
    }

    if (event.key === ']' && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault()
      const visibleButton = document.querySelector('.cpanel__switch')

      if (visibleButton) {
        visibleButton.click();
      }
      return;
    }
  }

  clickFirst(selectors) {
    for (const selector of selectors) {
      const el = document.querySelector(selector)
      if (el) {
        el.click()
        return
      }
    }
  }

  isUserTyping() {
    const activeElement = document.activeElement
    if (!activeElement) return false

    // `isContentEditable` covers contenteditable editors (e.g. the Hudini
    // composer), where single-key shortcuts like "/" are real typed characters.
    if (activeElement.isContentEditable) return true

    const tagName = activeElement.tagName
    if (["INPUT", "TEXTAREA", "SELECT", "IFRAME"].includes(tagName)) return true

    return !!activeElement.closest(".tiptap")
  }
}
